import React, { FC, ReactElement, useMemo } from 'react'
import TextField from '@material-ui/core/TextField/TextField'
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as CircleArrowIcon } from 'assets/svg/CircleArrowBottomIcon.svg'
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason } from '@material-ui/lab/Autocomplete/Autocomplete'
import { useQuery } from 'react-query'
import { Value } from '@material-ui/lab'
import { useMultipleAutocompleteStyles } from 'components/RolesAutoComplete'
import cn from 'classnames'
import { getSyndicatorsWithBalance } from 'services/syndicators'
import { IGetSyndicatorParams, ISyndicatorWithBalance } from 'typescript/interfaces/syndicators'
import makeStyles from '@material-ui/core/styles/makeStyles'

makeStyles(theme => ({
  optionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  optionName: {
    flex: 1,
  },
  balanceIndicator: {
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(0.5),
    },
  },
  balanceDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginLeft: theme.spacing(1),
  },
  sufficientBalance: {
    backgroundColor: theme.palette.success.main || '#4caf50',
  },
  insufficientBalance: {
    backgroundColor: theme.palette.warning.main || '#DCAC00',
  },
  tooltipContent: {
    fontSize: '0.875rem',
  },
}))

interface IAutoCompleteProps {
  syndicatorId: any | any[]
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: Value<any, any, any, any>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>
  ) => void
  error?: boolean
  helperText?: string | false
  label?: string | ReactElement
  multiple?: boolean
  disabled?: boolean
  paramsRequest?: IGetSyndicatorParams
}

const SyndicatorAutoCompleteWithBalance: FC<IAutoCompleteProps> = ({
  syndicatorId,
  onChange,
  helperText,
  error,
  label = 'Syndicator',
  multiple,
  disabled,
  paramsRequest = {},
}) => {
  const classes = useMultipleAutocompleteStyles()
  const { data, isLoading } = useQuery(['syndicators-with-balance'], () => getSyndicatorsWithBalance({ PageSize: 4000, ...paramsRequest }))

  const arraySyndicators = useMemo(() => {
    const dataSyndicators: ISyndicatorWithBalance[] = data ? data.data : []
    return dataSyndicators
  }, [data])

  const selectedOptionSyndicator = useMemo(() => {
    if (data) {
      const findCompany = arraySyndicators.find(c => c.id === syndicatorId) || null
      return findCompany
    }
    return null
  }, [arraySyndicators, syndicatorId])

  const arraySelectedOptions = useMemo(() => {
    if (Array.isArray(syndicatorId) && data) {
      return arraySyndicators.filter(c => syndicatorId.includes(c.id))
    }
    return []
  }, [arraySyndicators, syndicatorId])

  return (
    <Autocomplete
      disabled={disabled}
      multiple={Boolean(multiple)}
      value={multiple ? arraySelectedOptions : selectedOptionSyndicator}
      loading={isLoading}
      options={arraySyndicators}
      classes={{ inputRoot: cn(classes.inputRoot, multiple) }}
      getOptionLabel={(option: ISyndicatorWithBalance) => {
        return option.name
      }}
      getOptionSelected={(option: ISyndicatorWithBalance, value) => {
        if (value && value.id) return option.id === value.id
        return false
      }}
      onChange={onChange}
      renderInput={params => (
        <TextField
          {...params}
          autoComplete="off"
          helperText={helperText}
          error={error}
          label={label}
          InputProps={{
            ...params.InputProps,
            ref: params.InputProps.ref,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                <SvgIcon fontSize="inherit" viewBox="0 0 20 20" component={CircleArrowIcon} />
              </>
            ),
          }}
          variant="outlined"
        />
      )}
    />
  )
}

export default SyndicatorAutoCompleteWithBalance
